import React from "react";
import DownloadButtonsGroup from "./DownloadButtonsGroup";

const categories = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/>
        <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
        <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
      </svg>
    ),
    title: "Ammo",
    desc: "Snap a photo to instantly log caliber, brand, and count. Track every round across every caliber you own.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: "Firearms",
    desc: "Log your collection with serial numbers, maintenance records, and acquisition dates. Everything in one place.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.07 4.93l-1.41 1.41M5.34 18.66l-1.41 1.41M20 12h2M2 12h2M19.07 19.07l-1.41-1.41M5.34 5.34L3.93 3.93M12 20v2M12 2v2"/>
      </svg>
    ),
    title: "Reloading",
    desc: "Track powder, primers, brass, and dies. Know exactly what you have before you sit down at the bench.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C8.5 7 5 10.5 5 14a7 7 0 0014 0c0-3.5-3.5-7-7-12z"/>
      </svg>
    ),
    title: "Water",
    desc: "Monitor stored water supply, container types, and rotation schedules. Stay ready for any situation.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    title: "Medical",
    desc: "Track first aid supplies, medications, and expiration dates. Be prepared when it matters most.",
  },
];

const AppScreensSection: React.FC = () => (
  <section className="w-full bg-[#000000] py-24 px-4">
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-16">
        <p className="text-xs font-bold uppercase tracking-widest text-[#3b82f6] mb-3">Complete Preparedness Inventory</p>
        <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
          Everything You Own.<br className="hidden md:block" /> Nothing You Share.
        </h2>
        <p className="text-lg text-[#a3a3a3] max-w-2xl mx-auto">
          AmmoPantryAI tracks your full preparedness inventory — ammo, firearms, reloading supplies, medical gear, and water — all in one private app. On your device. No cloud.
        </p>
      </div>

      {/* Video + Categories */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

        {/* Phone mockup with video */}
        <div className="flex-shrink-0 flex flex-col items-center gap-4">
          <p className="text-xs font-bold uppercase tracking-widest text-[#3b82f6]">See it in action</p>
          <div
            className="relative rounded-[2.8rem] overflow-hidden border-[6px] border-[#2a2a2a] shadow-2xl shadow-black/60"
            style={{ width: 240, height: 520 }}
          >
            <video
              src="/ammopantryai-demo.mp4"
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
            {/* Notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-6 bg-[#2a2a2a] rounded-b-2xl" />
          </div>
          <p className="text-xs text-[#a3a3a3] text-center max-w-[200px]">Full inventory built from a photo in seconds</p>
        </div>

        {/* Feature categories grid */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
          {categories.map((cat) => (
            <div
              key={cat.title}
              className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 hover:border-[#3b82f6]/40 transition"
            >
              <div className="w-10 h-10 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center text-[#3b82f6] mb-3">
                {cat.icon}
              </div>
              <div className="text-lg font-bold text-white mb-1">{cat.title}</div>
              <div className="text-sm text-[#a3a3a3] leading-relaxed">{cat.desc}</div>
            </div>
          ))}
          {/* CTA card */}
          <div className="bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-2xl p-6 flex flex-col justify-between">
            <div>
              <div className="text-lg font-bold text-white mb-2">Ready to get organized?</div>
              <div className="text-sm text-[#a3a3a3] leading-relaxed mb-4">
                Available now on iOS and Android.
              </div>
            </div>
            <DownloadButtonsGroup variant="white" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default AppScreensSection;
